import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, Spinner, Button } from '../../components/ui';
import { 
  DollarSign, Save, RefreshCw, Edit2, CheckCircle, 
  AlertCircle, TrendingUp, CreditCard, Users
} from 'lucide-react';
import './Admin.css';

const AdminPricing = () => {
  const [registrationFee, setRegistrationFee] = useState('');
  const [creditPrice, setCreditPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const { request } = useApi();

  const [editRegFee, setEditRegFee] = useState(false);
  const [editCreditPrice, setEditCreditPrice] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await request('get', '/api/admin/pricing');
      if (data.success) {
        setRegistrationFee(data.registrationFee);
        setCreditPrice(data.creditPrice);
        setLastUpdated(new Date());
      } else {
        setError(data.error || 'Failed to fetch pricing');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRegistrationFee = async () => {
    const amount = parseFloat(registrationFee);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await request('put', '/api/admin/pricing/registration-fee', {
        amount: amount,
        active: true
      });

      if (response.success) {
        setSuccess('✅ Registration fee updated successfully to TZS ' + Number(response.amount).toLocaleString());
        setRegistrationFee(response.amount);
        setEditRegFee(false);
        setLastUpdated(new Date());
        
        setTimeout(() => fetchPricing(), 500);
      } else {
        setError(response.error || 'Failed to update registration fee');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update registration fee');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCreditPrice = async () => {
    const amount = parseFloat(creditPrice);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await request('put', '/api/admin/pricing/credit-price', {
        amount: amount,
        active: true
      });

      if (response.success) {
        setSuccess('✅ Credit price updated successfully to TZS ' + Number(response.amount).toLocaleString());
        setCreditPrice(response.amount);
        setEditCreditPrice(false);
        setLastUpdated(new Date());
        
        setTimeout(() => fetchPricing(), 500);
      } else {
        setError(response.error || 'Failed to update credit price');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update credit price');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '—';
    return 'TZS ' + Number(value).toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">💰 Pricing Management</h1>
          <p className="admin-subtitle">Manage registration fees and credit pricing across the platform</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="refresh-btn" onClick={fetchPricing}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {success && (
        <div className="alert-success animate-fade-in" style={{ 
          padding: '12px 16px', 
          borderRadius: '10px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: '#ECFDF5',
          color: '#065F46',
          border: '1px solid #A7F3D0'
        }}>
          <CheckCircle size={18} />
          <span>{success}</span>
          <button 
            onClick={() => setSuccess('')} 
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="alert-error animate-fade-in" style={{ 
          padding: '12px 16px', 
          borderRadius: '10px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: '#FEF2F2',
          color: '#991B1B',
          border: '1px solid #FCA5A5'
        }}>
          <AlertCircle size={18} />
          <span>{error}</span>
          <button 
            onClick={() => setError('')} 
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
      )}

      {lastUpdated && (
        <div style={{ 
          fontSize: '12px', 
          color: '#94A3B8', 
          marginBottom: '20px',
          textAlign: 'right'
        }}>
          Last updated: {lastUpdated.toLocaleString()}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <Card className="pricing-card" style={{ padding: '24px', border: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '44px', 
                height: '44px', 
                borderRadius: '12px', 
                background: '#E0F2FE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284C7'
              }}>
                <Users size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>Registration Fee</h3>
                <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>One-time fee per organization</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                fontSize: '11px', 
                background: '#D1FAE5', 
                color: '#065F46', 
                padding: '2px 10px', 
                borderRadius: '20px',
                fontWeight: 500
              }}>
                Active
              </span>
            </div>
          </div>

          {editRegFee ? (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#64748B' }}>TZS</span>
                <input
                  type="number"
                  className="pricing-input"
                  value={registrationFee}
                  onChange={(e) => setRegistrationFee(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    fontSize: '18px',
                    fontWeight: 600,
                    border: '2px solid #0284C7',
                    borderRadius: '8px',
                    outline: 'none',
                    background: '#F8FAFC'
                  }}
                  autoFocus
                  min="1"
                  step="100"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                <Button 
                  onClick={handleUpdateRegistrationFee} 
                  loading={saving}
                  style={{ flex: 1 }}
                >
                  <Save size={16} /> Save Changes
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setEditRegFee(false);
                    fetchPricing();
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: '12px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '16px 20px',
                background: '#F8FAFC',
                borderRadius: '10px',
                border: '1px solid #E2E8F0'
              }}>
                <span style={{ fontSize: '24px', fontWeight: 700, color: '#0F172A' }}>
                  {formatCurrency(registrationFee)}
                </span>
                <button
                  onClick={() => setEditRegFee(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#475569',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#0284C7';
                    e.currentTarget.style.color = '#0284C7';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.color = '#475569';
                  }}
                >
                  <Edit2 size={14} /> Edit
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#94A3B8', marginTop: '8px' }}>
                This fee is charged once when an organization registers on the platform
              </p>
            </div>
          )}
        </Card>

        <Card className="pricing-card" style={{ padding: '24px', border: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '44px', 
                height: '44px', 
                borderRadius: '12px', 
                background: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#D97706'
              }}>
                <CreditCard size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>Credit Price</h3>
                <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>Price per 1 credit (1 message)</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                fontSize: '11px', 
                background: '#D1FAE5', 
                color: '#065F46', 
                padding: '2px 10px', 
                borderRadius: '20px',
                fontWeight: 500
              }}>
                Active
              </span>
            </div>
          </div>

          {editCreditPrice ? (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#64748B' }}>TZS</span>
                <input
                  type="number"
                  className="pricing-input"
                  value={creditPrice}
                  onChange={(e) => setCreditPrice(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    fontSize: '18px',
                    fontWeight: 600,
                    border: '2px solid #D97706',
                    borderRadius: '8px',
                    outline: 'none',
                    background: '#F8FAFC'
                  }}
                  autoFocus
                  min="1"
                  step="10"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                <Button 
                  onClick={handleUpdateCreditPrice} 
                  loading={saving}
                  style={{ flex: 1 }}
                >
                  <Save size={16} /> Save Changes
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setEditCreditPrice(false);
                    fetchPricing();
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: '12px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '16px 20px',
                background: '#F8FAFC',
                borderRadius: '10px',
                border: '1px solid #E2E8F0'
              }}>
                <span style={{ fontSize: '24px', fontWeight: 700, color: '#0F172A' }}>
                  {formatCurrency(creditPrice)}
                </span>
                <button
                  onClick={() => setEditCreditPrice(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#475569',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#D97706';
                    e.currentTarget.style.color = '#D97706';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.color = '#475569';
                  }}
                >
                  <Edit2 size={14} /> Edit
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#94A3B8', marginTop: '8px' }}>
                This price is used to calculate the total cost when organizations purchase credits
              </p>
            </div>
          )}
        </Card>
      </div>

      <Card style={{ marginTop: '24px', padding: '20px 24px', background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <TrendingUp size={18} style={{ color: '#0284C7' }} />
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: '#0F172A' }}>Impact Preview</h4>
            <p style={{ fontSize: '13px', color: '#64748B', margin: '4px 0 0 0' }}>
              Registration: <strong style={{ color: '#0F172A' }}>{formatCurrency(registrationFee)}</strong> 
              {' · '} 
              Credit Price: <strong style={{ color: '#0F172A' }}>{formatCurrency(creditPrice)}</strong> 
              {' · '} 
              <span style={{ color: '#0284C7' }}>
                Example: 100 credits = {formatCurrency(Number(creditPrice) * 100)}
              </span>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminPricing;